import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useSyncExternalStore } from "react";
import i18n from "./index";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import {
  type AppLocale,
  canShowLanguageToggle,
  electionsLocaleDefaults,
  isPublicReportPath,
  readStoredLocale,
  resolveAppLocale,
  setStoredLocale,
  subscribeStoredLocale,
} from "./locale";

type PageScope = {
  publicSurface: boolean;
  companySlug: string | null;
};

const EMPTY_SCOPE: PageScope = { publicSurface: false, companySlug: null };

type LocaleContextValue = {
  locale: AppLocale;
  publicSurface: boolean;
  showToggle: boolean;
  setLocale: (locale: AppLocale) => void;
  setPageScope: (scope: PageScope) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const stored = useSyncExternalStore(subscribeStoredLocale, readStoredLocale, () => "es" as AppLocale);
  const [pageScope, setPageScopeState] = useState<PageScope>(EMPTY_SCOPE);

  const { data: company } = useQuery({
    queryKey: ["locale-my-company", user?.companyId],
    queryFn: () => apiService.getMyCompany(),
    enabled: !!user?.companyId,
    staleTime: 5 * 60 * 1000,
  });

  const setPageScope = useCallback((scope: PageScope) => {
    setPageScopeState(scope);
  }, []);

  const pathPublic = isPublicReportPath(location.pathname);
  const electionsScope = electionsLocaleDefaults(location.pathname);
  const publicSurface =
    pathPublic || pageScope.publicSurface || electionsScope.publicSurface;
  const langParam = new URLSearchParams(location.search).get("lang");
  const viewerCompanySlug = company?.slug ?? null;
  const reportCompanySlug = pageScope.companySlug ?? electionsScope.companySlug;

  const locale = resolveAppLocale({
    stored,
    viewerCompanySlug,
    reportCompanySlug,
    publicSurface,
    langParam,
    defaultLocale: electionsScope.defaultLocale,
  });

  const showToggle = publicSurface
    ? canShowLanguageToggle(reportCompanySlug)
    : canShowLanguageToggle(viewerCompanySlug);

  useEffect(() => {
    void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback(
    (next: AppLocale) => {
      setStoredLocale(next);
      if (!publicSurface) return;
      const params = new URLSearchParams(location.search);
      if (next === "en") params.set("lang", "en");
      else if (electionsLocaleDefaults(location.pathname).defaultLocale === "en") {
        params.set("lang", "es");
      } else params.delete("lang");
      const search = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          hash: location.hash,
        },
        { replace: true },
      );
    },
    [location.hash, location.pathname, location.search, navigate, publicSurface],
  );

  const value = useMemo(
    () => ({ locale, publicSurface, showToggle, setLocale, setPageScope }),
    [locale, publicSurface, setLocale, setPageScope, showToggle],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return value;
}

/** Public pages call this so the Podscope toggle and saved preference can apply. */
export function useRegisterPageLocale(
  companySlug: string | null | undefined,
  publicSurface = true,
) {
  const { setPageScope } = useLocale();
  useEffect(() => {
    setPageScope({
      publicSurface,
      companySlug: companySlug ?? null,
    });
    return () => setPageScope(EMPTY_SCOPE);
  }, [companySlug, publicSurface, setPageScope]);
}
